import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';
import SiteNav from './SiteNav';
import BlogFooter from './BlogFooter';
import SEO from './SEO';

// ─── Composants internes ──────────────────────────────────────────────────────

function ArticleLabel({ number }: { number: string }) {
  return (
    <span className="block text-[11px] tracking-[2px] uppercase text-amber font-medium mb-2">
      Article {number}
    </span>
  );
}

function ArticleTitle({ children }: { children: React.ReactNode }) {
  return (
    <h2 className="font-serif text-2xl font-normal text-ink mb-4 leading-snug">
      {children}
    </h2>
  );
}

function SubTitle({ children }: { children: React.ReactNode }) {
  return (
    <h3 className="font-serif text-lg font-normal text-ink mb-3 mt-6">
      {children}
    </h3>
  );
}

function Body({ children, className = '' }: { children: React.ReactNode; className?: string }) {
  return (
    <p className={`text-base text-ink/80 leading-relaxed ${className}`}>
      {children}
    </p>
  );
}

function InfoBlock({ children }: { children: React.ReactNode }) {
  return (
    <div className="bg-sand/20 border-l-4 border-amber px-5 py-4 rounded-r-sm space-y-1.5 my-4">
      {children}
    </div>
  );
}

function Separator() {
  return <div className="border-b border-ink/15 my-10" />;
}

// ─── Composant Navigation Croisée ─────────────────────────────────────────────

function LegalNav() {
  return (
    <div className="mt-16 pt-8 border-t border-ink/10 text-center">
      <p className="text-sm text-taupe/60 mb-4">Voir aussi :</p>
      <nav className="flex flex-wrap items-center justify-center gap-x-4 gap-y-2">
        <span className="text-sm text-ink/40 font-medium cursor-default">
          Confidentialité
        </span>
        <span className="text-ink/20 text-xs">|</span>
        <Link to="/fr/conditions-utilisation" className="text-sm text-amber hover:underline transition-colors font-medium">
          Conditions d'utilisation
        </Link>
        <span className="text-ink/20 text-xs">|</span>
        <Link to="/fr/mentions-legales" className="text-sm text-amber hover:underline transition-colors font-medium">
          Mentions légales
        </Link>
      </nav>
    </div>
  );
}

// ─── Page ─────────────────────────────────────────────────────────────────────

export default function PrivacyPolicy() {
  return (
    <div className="min-h-screen bg-paper font-sans">
      <SEO
        title="Politique de confidentialité | Amargi"
        description="Politique de confidentialité du site Amargi — données collectées, finalités, durées de conservation et exercice de vos droits (RGPD)."
        path="/confidentialite"
      />
      <SiteNav />
      <main className="pt-20 pb-24">
        <div className="max-w-3xl mx-auto px-6 md:px-12">

          {/* Retour */}
          <Link
            to="/fr/"
            className="inline-flex items-center gap-2 text-taupe/60 hover:text-amber text-sm mb-10 transition-colors"
          >
            <ArrowLeft className="w-4 h-4" />
            Retour au site
          </Link>

          {/* En-tête */}
          <div className="pb-10 border-b border-ink/15 mb-12">
            <span className="block text-[11px] tracking-[2px] uppercase text-amber mb-3">
              Protection des données personnelles
            </span>
            <h1 className="font-serif text-4xl font-light text-ink mb-3 leading-tight">
              Politique de confidentialité
            </h1>
            <p className="text-sm text-taupe/60 italic">Dernière mise à jour : juin 2025 (Conforme au RGPD UE 2016/679)</p>
          </div>

          <InfoBlock>
            <Body>
              Nous avons rédigé cette politique pour être lue, pas pour décourager la lecture. En langage clair : nous collectons le minimum nécessaire, nous ne vendons aucune donnée, et vous pouvez exercer vos droits à tout moment en nous écrivant.
            </Body>
          </InfoBlock>

          {/* ── Article 01 ── Responsable ──────────────────────────────────── */}
          <article className="mb-10 mt-10">
            <ArticleLabel number="01" />
            <ArticleTitle>Responsable du traitement</ArticleTitle>
            <Body className="mb-4">
              Above The Clouds est responsable du traitement de vos données personnelles collectées via le site amargi.me et l'outil Amargi.
            </Body>
            <div className="border border-ink/20 rounded-md px-7 py-6 space-y-1.5">
              <p className="text-ink text-base font-medium">Above The Clouds (Bertrand Jacob)</p>
              <p className="text-ink/70 text-base">Rue de la Hulpe 105A, 1331 Rosières, Belgique</p>
              <p className="text-ink/70 text-base">BCE : BE 1022.831.049</p>
              <p className="text-ink/70 text-base">
                Contact :{' '}
                <a href="mailto:contact@amargi.me" className="text-amber hover:underline transition-colors font-medium">
                  contact@amargi.me
                </a>
              </p>
            </div>
          </article>

          <Separator />

          {/* ── Article 02 ── Données collectées ───────────────────────────── */}
          <article className="mb-10">
            <ArticleLabel number="02" />
            <ArticleTitle>Données collectées, finalités et bases légales</ArticleTitle>
            <Body className="mb-6">
              Nous collectons uniquement les données nécessaires aux finalités décrites ci-dessous. Aucune donnée n'est collectée sans raison légitime.
            </Body>

            <div className="overflow-x-auto mb-8 border border-ink/15 rounded-md">
              <table className="w-full border-collapse text-sm">
                <thead>
                  <tr className="bg-ink/5 text-ink">
                    <th className="px-4 py-3 text-left font-medium border-b border-ink/15">Données</th>
                    <th className="px-4 py-3 text-left font-medium border-b border-ink/15">Finalité</th>
                    <th className="px-4 py-3 text-left font-medium border-b border-ink/15">Base légale</th>
                    <th className="px-4 py-3 text-left font-medium border-b border-ink/15">Conservation</th>
                  </tr>
                </thead>
                <tbody>
                  {[
                    ['Nom, prénom, email', 'Envoi de ressources, communications Amargi', 'Consentement explicite', "Jusqu'au retrait ou 3 ans sans activité"],
                    ['Réponses au questionnaire', 'Profil autonome, personnalisation, amélioration', 'Exécution du contrat / Consentement', "Durée d'utilisation + 1 an"],
                    ['Données de paiement', 'Traitement des transactions', 'Exécution du contrat', 'Traitées par Stripe (non stockées par Amargi)'],
                    ['Données de navigation', 'Mesure audience, amélioration du site', 'Consentement (cookies)', '26 mois maximum'],
                    ['Emails entrants', 'Réponse à vos demandes', 'Intérêt légitime', '3 ans depuis le dernier échange'],
                    ['Historique de sessions', 'Suivi de l’évolution, personnalisation des exercices', 'Consentement explicite', 'Durée d’utilisation + 1 an'],
                    ['Identifiant anonyme', 'Lier les sessions sans compte obligatoire', 'Intérêt légitime', 'Jusqu’à suppression manuelle ou 1 an sans activité'],
                  ].map(([data, purpose, basis, retention], i) => (
                    <tr key={i} className="border-b border-ink/10 last:border-0">
                      <td className="px-4 py-3 align-top text-ink/90 font-medium">{data}</td>
                      <td className="px-4 py-3 align-top text-ink/70">{purpose}</td>
                      <td className="px-4 py-3 align-top text-ink/70">{basis}</td>
                      <td className="px-4 py-3 align-top text-ink/70">{retention}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            <SubTitle>Ce que nous ne faisons pas</SubTitle>
            <ul className="space-y-3 mb-6">
              {[
                'Nous ne vendons pas vos données à des tiers.',
                "Nous n'utilisons pas vos données à des fins de profilage commercial.",
                'Nous ne collectons pas de données sensibles sans consentement explicite renforcé.',
              ].map((item, i) => (
                <li key={i} className="flex items-start gap-3 text-base text-ink/80">
                  <span className="text-amber mt-0.5 shrink-0">→</span>{item}
                </li>
              ))}
            </ul>

            <InfoBlock>
              <Body>
                <strong>Note sur les données de l'outil :</strong> Les réponses au questionnaire peuvent être de nature sensible. Elles sont traitées avec un niveau de protection renforcé et ne sont jamais partagées individuellement. Elles peuvent être utilisées sous forme agrégée et anonymisée pour améliorer les protocoles.
              </Body>
            </InfoBlock>
          </article>

          <Separator />

          {/* ── Article 03 ── Sous-traitants ───────────────────────────────── */}
          <article className="mb-10">
            <ArticleLabel number="03" />
            <ArticleTitle>Sous-traitants et transferts hors UE</ArticleTitle>
            <Body className="mb-6">
              Nous faisons appel aux prestataires suivants. Chacun est soumis à des obligations contractuelles de protection conformes au RGPD.
            </Body>

            <div className="overflow-x-auto mb-6 border border-ink/15 rounded-md">
              <table className="w-full border-collapse text-sm">
                <thead>
                  <tr className="bg-ink/5 text-ink">
                    <th className="px-4 py-3 text-left font-medium border-b border-ink/15">Prestataire</th>
                    <th className="px-4 py-3 text-left font-medium border-b border-ink/15">Rôle</th>
                    <th className="px-4 py-3 text-left font-medium border-b border-ink/15">Localisation</th>
                    <th className="px-4 py-3 text-left font-medium border-b border-ink/15">Garantie RGPD</th>
                  </tr>
                </thead>
                <tbody>
                  {[
                    { name: 'Vercel Inc.', role: 'Hébergement du site', loc: 'États-Unis', guarantee: 'CCT / EU-US DPF' },
                    { name: 'Supabase, Inc.', role: 'Base de données, authentification', loc: 'UE (Frankfurt)', guarantee: 'DPA + CCT' },
                    { name: 'Stripe, Inc.', role: 'Traitement des paiements', loc: 'États-Unis / UE', guarantee: 'CCT + certifié RGPD' },
                    { name: 'Kajabi, LLC', role: 'Envoi d\'e-mails', loc: 'États-Unis', guarantee: 'CCT' },
                    { name: 'Google Analytics', role: "Mesure d'audience", loc: 'États-Unis', guarantee: 'CCT + consentement requis' },
                  ].map((row, i) => (
                    <tr key={i} className="border-b border-ink/10 last:border-0">
                      <td className="px-4 py-3 align-top text-ink/90 font-medium">{row.name}</td>
                      <td className="px-4 py-3 align-top text-ink/70">{row.role}</td>
                      <td className="px-4 py-3 align-top text-ink/70">{row.loc}</td>
                      <td className="px-4 py-3 align-top text-ink/70">{row.guarantee}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </article>

          <Separator />

          {/* ── Article 04 ── Cookies ──────────────────────────────────────── */}
          <article className="mb-10">
            <ArticleLabel number="04" />
            <ArticleTitle>Cookies et traceurs</ArticleTitle>

            <SubTitle>Cookies strictement nécessaires</SubTitle>
            <Body className="mb-4">
              <span className="inline-block text-[10px] tracking-[2px] uppercase px-2 py-0.5 rounded-sm bg-ink/10 text-ink font-medium mr-2">Obligatoire</span>
              Indispensables au fonctionnement du site (authentification, sécurité, session de paiement Stripe). Ne nécessitent pas votre consentement.
            </Body>

            <SubTitle>Cookies analytiques</SubTitle>
            <Body className="mb-4">
              <span className="inline-block text-[10px] tracking-[2px] uppercase px-2 py-0.5 rounded-sm bg-amber/10 text-amber font-medium mr-2">Consentement requis</span>
              Google Analytics 4 (mesure des visites, pages, durée). Durée de conservation : 26 mois maximum.
            </Body>

            <SubTitle>Gérer vos préférences</SubTitle>
            <Body>
              Vous pouvez modifier vos préférences via le bandeau de gestion des cookies en bas de chaque page, ou en configurant votre navigateur.
            </Body>
          </article>

          <Separator />

          {/* ── Article 05 ── Droits ───────────────────────────────────────── */}
          <article className="mb-10">
            <ArticleLabel number="05" />
            <ArticleTitle>Vos droits</ArticleTitle>
            <Body className="mb-6">
              Conformément au RGPD et à la loi belge du 30 juillet 2018, vous disposez des droits suivants :
            </Body>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-8">
              {[
                { title: "Droit d'accès", body: 'Obtenir la confirmation que des données vous concernant sont traitées, et en recevoir une copie.' },
                { title: 'Droit de rectification', body: 'Faire corriger des données inexactes ou incomplètes vous concernant.' },
                { title: "Droit à l'effacement", body: 'Demander la suppression de vos données dans les conditions prévues par le RGPD (art. 17).' },
                { title: 'Droit à la portabilité', body: 'Recevoir vos données dans un format structuré, couramment utilisé et lisible par machine.' },
                { title: "Droit d'opposition", body: 'Vous opposer au traitement de vos données, notamment à des fins de prospection commerciale.' },
                { title: 'Droit à la limitation', body: 'Demander la suspension temporaire du traitement dans certaines circonstances.' },
              ].map((right, i) => (
                <div key={i} className="border border-ink/15 rounded-md px-5 py-4">
                  <div className="flex items-center gap-2 mb-2">
                    <span className="w-1.5 h-1.5 rounded-full bg-amber shrink-0" />
                    <span className="text-sm font-medium text-ink">{right.title}</span>
                  </div>
                  <p className="text-sm text-ink/70 leading-relaxed">{right.body}</p>
                </div>
              ))}
            </div>

            <SubTitle>Comment exercer vos droits</SubTitle>
            <Body className="mb-6">
              Adressez votre demande par email à{' '}
              <a href="mailto:contact@amargi.me" className="text-amber hover:underline transition-colors font-medium">
                contact@amargi.me
              </a>. Nous nous engageons à vous répondre dans un délai maximum de 30 jours.
            </Body>

            <SubTitle>Droit de réclamation</SubTitle>
            <div className="border border-ink/20 rounded-md px-7 py-6 space-y-1.5 mt-4">
              <p className="text-ink text-base font-medium">Autorité de Protection des Données (APD)</p>
              <p className="text-ink/70 text-base">Rue de la Presse 35, 1000 Bruxelles, Belgique</p>
              <p className="text-ink/70 text-base">Tel : +32 (0)2 274 48 00</p>
              <p className="text-ink/70 text-base">
                Site :{' '}
                <a href="https://www.autoriteprotectiondonnees.be" target="_blank" rel="noopener noreferrer" className="text-amber hover:underline transition-colors font-medium">
                  autoriteprotectiondonnees.be
                </a>
              </p>
            </div>
          </article>

          <Separator />

          {/* ── Article 06 ── Sécurité ─────────────────────────────────────── */}
          <article className="mb-10">
            <ArticleLabel number="06" />
            <ArticleTitle>Sécurité des données</ArticleTitle>
            <ul className="space-y-3 mb-6">
              {[
                'Connexion chiffrée HTTPS sur l\'ensemble du site.',
                'Accès aux données restreint aux personnes habilitées.',
                'Hébergement sécurisé via Vercel et Supabase.',
                'Données de paiement traitées exclusivement par Stripe (certifié PCI-DSS).',
              ].map((item, i) => (
                <li key={i} className="flex items-start gap-3 text-base text-ink/80">
                  <span className="text-amber mt-0.5 shrink-0">→</span>{item}
                </li>
              ))}
            </ul>
            <Body>
              En cas de violation de données, nous notifierons l'APD belge dans les 72 heures, et vous informerons si le risque est élevé.
            </Body>
          </article>

          <Separator />

          {/* ── Article 07 ── Mineurs ──────────────────────────────────────── */}
          <article className="mb-10">
            <ArticleLabel number="07" />
            <ArticleTitle>Mineurs</ArticleTitle>
            <Body>
              Le site amargi.me est destiné à un public adulte. Nous ne collectons pas sciemment de données concernant des personnes de moins de 16 ans. Contactez-nous à{' '}
              <a href="mailto:contact@amargi.me" className="text-amber hover:underline transition-colors font-medium">
                contact@amargi.me
              </a>{' '}
              si vous pensez que votre enfant nous a fourni des données.
            </Body>
          </article>

          <Separator />

          {/* ── Article 08 ── Modifications ────────────────────────────────── */}
          <article className="mb-12">
            <ArticleLabel number="08" />
            <ArticleTitle>Modifications de cette politique</ArticleTitle>
            <Body>
              Nous nous réservons le droit de modifier cette politique à tout moment. La version en vigueur est toujours celle accessible sur cette page. En cas de modification substantielle, nous vous en informerons par email si vous êtes inscrit.
            </Body>
          </article>

          {/* Note de bas de page */}
          <div className="bg-amber/10 border border-amber/30 rounded-sm px-5 py-4 text-center mb-8">
            <p className="text-sm text-taupe/70 leading-relaxed">
              Cette politique est conforme au Règlement (UE) 2016/679 (RGPD) et à la loi belge du 30 juillet 2018.
            </p>
          </div>

          {/* Navigation croisée */}
          <LegalNav />

        </div>
      </main>
      <BlogFooter />
    </div>
  );
}
